import { api } from './api';

export interface NachforderungItem {
  id: string;
  label: string;
  originaltext: string;
  done: boolean;
}

export type NachforderungStatus = 'neu' | 'bestaetigt' | 'erledigt';

export interface Nachforderung {
  id: number;
  customerId: number;
  vorgangId: number | null;
  vorgangsnummer: string | null;
  kundeName: string | null;
  kundeAnsprechpartner: string | null;
  kundeTelefon: string | null;
  frist: string | null; // ISO
  /** Die beim Ingest aus der Mail erkannte Frist, unverändert seit dem Anlegen — Basis für
   *  den Abweichungs-Hinweis, wenn `frist` manuell überschrieben wurde. */
  fristUrspruenglich: string | null; // ISO
  status: NachforderungStatus;
  items: NachforderungItem[];
  note: string | null;
  originalMailText: string | null;
  createdAt: string;
  updatedAt: string;
}

export type Ampel = 'rot' | 'orange' | 'gruen' | 'pruefen';

/** Ampel-Logik: 🔴 überfällig · 🟠 ≤ 7 Tage · 🟢 mehr Luft · prüfen (keine Frist). */
export function ampelFor(frist: string | null, now: Date = new Date()): Ampel {
  if (!frist) return 'pruefen';
  const days = Math.ceil((new Date(frist).getTime() - now.getTime()) / 86400000);
  if (days < 0) return 'rot';
  if (days <= 7) return 'orange';
  return 'gruen';
}

/**
 * Sortierung: überfällig zuerst → ohne Frist (prüfen) → nach Frist aufsteigend,
 * und ganz unten alles, was vollständig abgehakt ist.
 *
 * Eine Karte mit allen Haken ist geliefert — ihre Frist ist damit gegenstandslos. Ohne
 * diesen letzten Rang stünde eine erledigte, längst überfällige Karte weiter ganz oben und
 * verdrängte die Fälle, die wirklich drängen. Karten ohne erkannte Punkte gelten NICHT als
 * fertig (dort ist nichts abzuhaken) und bleiben nach ihrer Frist einsortiert.
 */
export function sortNachforderungen<
  T extends { frist: string | null; items?: { done: boolean }[] },
>(list: T[], now: Date = new Date()): T[] {
  const vollstaendig = (n: T): boolean => !!n.items && n.items.length > 0 && n.items.every((it) => it.done);
  const rank = (n: T): number => {
    if (vollstaendig(n)) return 3;
    const a = ampelFor(n.frist, now);
    if (a === 'rot') return 0;
    if (a === 'pruefen') return 1;
    return 2;
  };
  return [...list].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    const fa = a.frist ? new Date(a.frist).getTime() : Infinity;
    const fb = b.frist ? new Date(b.frist).getTime() : Infinity;
    return fa - fb;
  });
}

export const listNachforderungen = () => api.get<Nachforderung[]>('/api/nachforderungen');

export const toggleItem = (id: number, itemId: string, done: boolean) =>
  api.patch<Nachforderung>(`/api/nachforderungen/${id}/items/${itemId}`, { done });

export const setNote = (id: number, note: string) =>
  api.patch<Nachforderung>(`/api/nachforderungen/${id}/note`, { note });

export const setStatus = (id: number, status: NachforderungStatus) =>
  api.patch<Nachforderung>(`/api/nachforderungen/${id}/status`, { status });

export const updateReview = (id: number, patch: { items?: NachforderungItem[]; frist?: string | null }) =>
  api.patch<Nachforderung>(`/api/nachforderungen/${id}/review`, patch);

/** Manuelle Zuordnung eines Vorgangs zu einem Eintrag ohne Vorgang. */
export const assignVorgang = (id: number, vorgangId: number) =>
  api.patch<Nachforderung>(`/api/nachforderungen/${id}/vorgang`, { vorgangId });

/**
 * Welche Nachforderungen sind durch eine NEUERE zum selben Vorgang überholt?
 *
 * Die BAFA schickt zu einem Vorgang mehrere Runden: erst drei Punkte, nach dem Nachreichen
 * bleibt einer davon offen — als neue Mail mit neuer Frist. Die ältere Karte listet dann
 * Punkte, die längst akzeptiert sind. Sie wird deshalb gedämpft und als überholt markiert,
 * statt sie zusammenzuführen: Fristen und Original-Mails bleiben so nachvollziehbar.
 *
 * Ohne Vorgangszuordnung (vorgangId null) gibt es keinen Bezug → nie überholt.
 * Liefert eine Zuordnung „Karten-Id → Datum der ablösenden Nachforderung".
 */
export function ueberholteNachforderungen<
  T extends NachforderungGruppierbar,
>(list: T[]): Map<number, string> {
  const neueste = new Map<string, T>();
  for (const n of list) {
    const key = nachforderungGruppe(n);
    if (key == null) continue;
    const bisher = neueste.get(key);
    const juenger = !bisher
      || new Date(n.createdAt).getTime() > new Date(bisher.createdAt).getTime()
      || (new Date(n.createdAt).getTime() === new Date(bisher.createdAt).getTime() && n.id > bisher.id);
    if (juenger) neueste.set(key, n);
  }
  const ueberholt = new Map<number, string>();
  for (const n of list) {
    const key = nachforderungGruppe(n);
    if (key == null) continue;
    const aktuell = neueste.get(key);
    if (aktuell && aktuell.id !== n.id) ueberholt.set(n.id, aktuell.createdAt);
  }
  return ueberholt;
}

export type NachforderungGruppierbar = {
  id: number;
  vorgangId: number | null;
  customerId?: number | null;
  createdAt: string;
  items?: { label: string }[];
};

/**
 * Schlüssel, unter dem mehrere Runden derselben Forderung zusammengehören — oder `null`,
 * wenn eine Karte für sich steht.
 *
 * Der Vorgang ist das stärkste Merkmal: Karten dazu gehören zusammen, egal wie sich die
 * Punkte zwischen den Runden verschieben.
 *
 * Ohne Vorgang (die BAFA schreibt, bevor der Vorgang angelegt ist) bleibt nur der Inhalt:
 * gleicher Kunde UND exakt dieselben Punkte. Das fängt den häufigen Fall ab, dass die BAFA
 * dieselbe Forderung per neuer Mail nachhakt. Bewusst nur bei GLEICHEM Punkte-Satz —
 * bei bloßer Überschneidung könnte ein noch offener Punkt in einer fremden Karte
 * verschwinden, und ohne Vorgang fehlt jeder Beleg, dass die Karten zusammengehören.
 * Karten ohne erkannte Punkte stehen immer für sich (es gibt nichts zu vergleichen).
 */
export function nachforderungGruppe(n: NachforderungGruppierbar): string | null {
  if (n.vorgangId != null) return `v:${n.vorgangId}`;
  if (n.customerId == null || !n.items || n.items.length === 0) return null;
  const punkte = n.items
    .map((it) => it.label.trim().toLowerCase())
    .sort()
    .join('|');
  return `c:${n.customerId}|${punkte}`;
}
